import { ACTIVE_RADIUS_M } from "./lod";
import { MATERIALS } from "./materials";
import { orbitalSpeed } from "./planet";
import type { PlanetSpec } from "./planets";
import { fillBatteriesAndTanks, makeShipModule, recomputeShipMass, shipExtent } from "./ship";
import { clamp } from "../util/math";
import type { AsteroidProxy, EntityId, Ship, ShipModule, World } from "./types";

/** Size of one asteroid block, in metres. */
const ASTEROID_CELL_SIZE_M = 4;

/** Relative abundance of each mineral block in a ring. Drives both how common a
 *  mineral is and (via the material's value) how rare/precious the rich finds
 *  are. Tune the ring's character here. */
const MINERAL_WEIGHTS: Record<string, number> = {
  rock: 70, iron: 14, copper: 8, uranium: 3, water: 5,
};

const MINERAL_MIX = MATERIALS
  .filter((m) => MINERAL_WEIGHTS[m.resource] > 0)
  .map((m) => ({ defId: m.blockId, weight: MINERAL_WEIGHTS[m.resource] }));
const MINERAL_TOTAL = MINERAL_MIX.reduce((s, m) => s + m.weight, 0);

export function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickMineral(rand: () => number): string {
  let r = rand() * MINERAL_TOTAL;
  for (const m of MINERAL_MIX) {
    r -= m.weight;
    if (r <= 0) return m.defId;
  }
  return MINERAL_MIX[0].defId;
}

/** Ore purity 0.05..1, triangular toward the middle (avg of two uniforms). */
function rollOreQuality(rand: () => number): number {
  return clamp((rand() + rand()) * 0.5, 0.05, 1);
}

interface AsteroidSpawn {
  x: number;
  y: number;
  /** Number of mineral blocks the asteroid is built from. */
  pieces: number;
  seed: number;
  vx?: number;
  vy?: number;
  rot?: number;
  angVel?: number;
}

const NEIGHBOURS = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
] as const;

/** Grows a connected blob of exactly `pieces` cells by random neighbour
 *  accretion, so asteroids read as solid organic clumps rather than perfect
 *  discs. Returns grid coords normalised to start at (0, 0). */
function growBlob(rand: () => number, pieces: number): Array<[number, number]> {
  const placed = new Set<string>();
  const cells: Array<[number, number]> = [];
  const frontier: Array<[number, number]> = [];
  const key = (x: number, y: number): string => `${x},${y}`;

  const place = (x: number, y: number): void => {
    placed.add(key(x, y));
    cells.push([x, y]);
    for (const [dx, dy] of NEIGHBOURS) {
      if (!placed.has(key(x + dx, y + dy))) frontier.push([x + dx, y + dy]);
    }
  };

  place(0, 0);
  while (cells.length < pieces && frontier.length > 0) {
    const i = Math.floor(rand() * frontier.length);
    const [fx, fy] = frontier[i];
    frontier[i] = frontier[frontier.length - 1];
    frontier.pop();
    if (placed.has(key(fx, fy))) continue;
    place(fx, fy);
  }

  let minX = Infinity, minY = Infinity;
  for (const [x, y] of cells) { if (x < minX) minX = x; if (y < minY) minY = y; }
  return cells.map(([x, y]) => [x - minX, y - minY]);
}

interface Kinematics {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  angVel: number;
}

/** Builds the full asteroid Ship deterministically from (seed, pieces). The
 *  rng sequence — blob shape, then per-cell mineral + ore quality — must stay
 *  in this exact order so a dehydrated body re-hydrates identically. */
function buildAsteroidShip(id: EntityId, seed: number, pieces: number, kin: Kinematics): Ship {
  const rand = makeRng(seed);
  const n = Math.max(1, Math.floor(pieces));

  const modules: ShipModule[] = growBlob(rand, n).map(
    ([gx, gy]) => makeShipModule(pickMineral(rand), gx, gy, 0, rollOreQuality(rand)),
  );
  if (modules.length === 0) {
    modules.push(makeShipModule("rock_block", 0, 0, 0, rollOreQuality(rand)));
  }

  const ship: Ship = {
    id,
    name: `Asteroid-${id}`,
    com: { x: 0, y: 0 },
    body: {
      pos: { x: kin.x, y: kin.y },
      vel: { x: kin.vx, y: kin.vy },
      rot: kin.rot,
      angVel: kin.angVel,
      mass: 0,
      inertia: 0,
    },
    modules,
    conduits: [],
    throttle: 0,
    steer: 0,
    strafe: 0,
    fire1: false,
    powered: false,
    autoBrake: false,
    fireMode: "combat",
    isAsteroid: true,
    cellSizeM: ASTEROID_CELL_SIZE_M,
    tradeWithShipId: null,
    boardedByShipId: null,
    credits: 0,
    collider: null,
    asteroidSeed: seed,
    asteroidPieces: n,
  };
  recomputeShipMass(ship);
  fillBatteriesAndTanks(ship);
  return ship;
}

/** Cheap closed-form bounding-radius estimate for a `pieces`-cell blob, used to
 *  size the dot / cull a never-hydrated asteroid without paying to build it.
 *  Replaced by the exact shipExtent the first time the body dehydrates. */
function estimateExtent(pieces: number, cell: number): number {
  return cell * (0.7 + 0.9 * Math.sqrt(Math.max(1, pieces)));
}

/** Spawns an asteroid in its dormant proxy form — no module data, just enough
 *  to orbit, draw, and regenerate on demand. */
function spawnAsteroidProxy(world: World, params: AsteroidSpawn): AsteroidProxy {
  const pieces = Math.max(1, Math.floor(params.pieces));
  const proxy: AsteroidProxy = {
    id: world.nextId++,
    pos: { x: params.x, y: params.y },
    vel: { x: params.vx ?? 0, y: params.vy ?? 0 },
    rot: params.rot ?? 0,
    angVel: params.angVel ?? 0,
    seed: params.seed,
    pieces,
    extent: estimateExtent(pieces, ASTEROID_CELL_SIZE_M),
  };
  world.asteroids.push(proxy);
  return proxy;
}

function proxyFromShip(ship: Ship): AsteroidProxy {
  return {
    id: ship.id,
    pos: { x: ship.body.pos.x, y: ship.body.pos.y },
    vel: { x: ship.body.vel.x, y: ship.body.vel.y },
    rot: ship.body.rot,
    angVel: ship.body.angVel,
    seed: ship.asteroidSeed ?? 0,
    pieces: ship.asteroidPieces ?? ship.modules.length,
    extent: shipExtent(ship),
  };
}

/** Lays out a planet's whole ring as dormant proxies on stable circular orbits.
 *  Radius is bell-weighted (average of three uniforms) so the band is densest
 *  at its centre and feathers out at the edges rather than reading as a
 *  hard-edged, computer-perfect belt. Called once at scene setup. */
export function populateRing(world: World, planet: PlanetSpec): void {
  const ring = planet.ring;
  const rand = makeRng(ring.seed);
  const pieceSpan = ring.maxPieces - ring.minPieces + 1;
  for (let i = 0; i < ring.count; i++) {
    const angle = rand() * Math.PI * 2;
    const bell = (rand() + rand() + rand()) / 3 * 2 - 1; // [-1, 1], centre-heavy
    const r = ring.centerM + bell * ring.halfWidthM;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const v = orbitalSpeed(world.planet, r);
    const pieces = ring.minPieces + Math.floor(rand() * pieceSpan);
    spawnAsteroidProxy(world, {
      x: cos * r,
      y: sin * r,
      pieces,
      seed: (rand() * 1e9) | 0,
      // Tangential velocity for a counter-clockwise circular orbit.
      vx: -sin * v,
      vy: cos * v,
      rot: rand() * Math.PI * 2,
      angVel: (rand() - 0.5) * 0.2,
    });
  }
}

/** Hysteresis: hydrate on entering the active zone, dehydrate only past a
 *  margin beyond it, so a body skimming the boundary doesn't thrash. */
const HYDRATE_R2 = ACTIVE_RADIUS_M * ACTIVE_RADIUS_M;
const DEHYDRATE_MARGIN_M = 400;
const DEHYDRATE_R2 = (ACTIVE_RADIUS_M + DEHYDRATE_MARGIN_M) ** 2;

/** Per-tick asteroid-field maintenance:
 *   1. Dehydrate pristine hydrated asteroids that have drifted out of range
 *      back into proxies (modified ones stay full Ships for good).
 *   2. Orbit-integrate every parked proxy under planet gravity and, in the same
 *      pass, hydrate any that have entered the active zone.
 *  The single fused O(N) proxy pass is the only cost that scales with the total
 *  asteroid count; everything else is bounded by how many are loaded. */
export function stepAsteroidField(
  world: World,
  dt: number,
  focusX: number,
  focusY: number,
  hasFocus: boolean,
): void {
  if (hasFocus) {
    let toDehydrate: Ship[] | null = null;
    for (const ship of world.ships.values()) {
      if (!ship.isAsteroid || ship.modified || ship.asteroidSeed == null) continue;
      const dx = ship.body.pos.x - focusX;
      const dy = ship.body.pos.y - focusY;
      if (dx * dx + dy * dy <= DEHYDRATE_R2) continue;
      (toDehydrate ??= []).push(ship);
    }
    if (toDehydrate) {
      for (const ship of toDehydrate) {
        world.asteroids.push(proxyFromShip(ship));
        world.ships.delete(ship.id);
      }
    }
  }

  const planet = world.planet;
  const arr = world.asteroids;
  // Reverse walk so hydration can swap-remove the current entry in place.
  for (let i = arr.length - 1; i >= 0; i--) {
    const a = arr[i];

    // Hydrate from the body's CURRENT state, before integrating it here — the
    // resulting Ship gets this tick's gravity + integration in the main loop,
    // so it advances exactly one dt rather than two.
    if (hasFocus) {
      const dx = a.pos.x - focusX;
      const dy = a.pos.y - focusY;
      if (dx * dx + dy * dy <= HYDRATE_R2) {
        const ship = buildAsteroidShip(a.id, a.seed, a.pieces,
          { x: a.pos.x, y: a.pos.y, vx: a.vel.x, vy: a.vel.y, rot: a.rot, angVel: a.angVel });
        world.ships.set(ship.id, ship);
        arr[i] = arr[arr.length - 1];
        arr.pop();
        continue;
      }
    }

    const dxp = planet.pos.x - a.pos.x;
    const dyp = planet.pos.y - a.pos.y;
    const r2 = dxp * dxp + dyp * dyp;
    if (r2 >= 1) {
      const r = Math.sqrt(r2);
      const acc = planet.gravParam / r2;
      a.vel.x += acc * (dxp / r) * dt;
      a.vel.y += acc * (dyp / r) * dt;
    }
    a.pos.x += a.vel.x * dt;
    a.pos.y += a.vel.y * dt;
    a.rot += a.angVel * dt;
  }
}
